import {
  ADMIN_ROLES,
  createLeadSchema,
  DocumentType,
  IDENTITY_MATCH_LABELS,
  MANAGER_ROLES,
  normalizeDocument,
  normalizeEmail,
  normalizePhone,
  resolveMediumCategory,
  resolveMediumName,
  type CheckIdentityMatch,
  type CheckIdentityResponse,
  type CreateLeadPayload,
  type CreateLeadResult,
} from '@performance-crm/shared';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CustomFieldInputs } from '@/components/leads/CustomFieldInputs';
import { customFormErrors, customPayload } from '@/components/leads/customFields';
import { TagChip } from '@/components/leads/TagChips';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast-context';
import { ApiRequestError } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import {
  useAdvisors,
  useCatalogs,
  useCheckIdentity,
  useCreateLead,
  useNextAdvisor,
  useProjects,
} from '@/lib/queries';
import { useCompanyScope } from '@/lib/company-context';

/** Valor del selector de medio que abre el texto libre. */
const OTHER_MEDIUM = '__other__';
/** Valor del selector de asesor que deja el lead en la bolsa a propósito (sin aplicar el turno). */
const POOL = '__pool__';
const NOTE_MAX = 2000;
const TEXT_MAX = 80;

/** Campo del formulario (id del elemento) al que pertenece cada ruta de error de la validación. */
const FIELD_IDS: Record<string, string> = {
  projectId: 'project',
  'contact.firstName': 'firstName',
  'contact.lastNamePaternal': 'lastNamePaternal',
  'contact.lastNameMaternal': 'lastNameMaternal',
  'contact.phone': 'phone',
  'contact.email': 'email',
  'contact.documentNumber': 'docNumber',
  'contact.district': 'district',
  medium: 'medium-other',
  note: 'note',
  'consent.reason': 'optout-reason',
};

/** Los datos completos con los que la API busca a la persona (los mismos umbrales que `identityKeysOf`). */
function identityProbe(f: {
  phone: string;
  email: string;
  documentNumber: string;
  documentType: string;
}): { documentNumber: string | null; phone: string | null; email: string | null } | null {
  const phone = normalizePhone(f.phone);
  const doc = normalizeDocument(f.documentNumber, f.documentType || null);
  const probe = {
    documentNumber: doc && doc.length >= 8 ? doc : null,
    phone: phone && phone.length === 9 ? phone : null,
    email: normalizeEmail(f.email),
  };
  return probe.documentNumber || probe.phone || probe.email ? probe : null;
}

export function NewLeadPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: projects } = useProjects();
  const companies = useMemo(
    () =>
      user?.memberships.filter(
        (m, i, a) =>
          m.companyActive !== false && a.findIndex((x) => x.companyId === m.companyId) === i,
      ) ?? [],
    [user],
  );
  const { active, setActive } = useCompanyScope();
  const [companyId, setCompanyId] = useState(active ?? companies[0]?.companyId ?? '');
  const effectiveCompany = companyId || companies[0]?.companyId || '';
  const { data: catalogs } = useCatalogs(effectiveCompany || undefined);
  const { data: advisors } = useAdvisors(effectiveCompany || undefined);
  const isManager = !!user?.memberships.some(
    (m) => m.companyId === effectiveCompany && MANAGER_ROLES.includes(m.role),
  );
  const isAdmin = !!user?.memberships.some(
    (m) => m.companyId === effectiveCompany && ADMIN_ROLES.includes(m.role),
  );
  const mutation = useCreateLead();
  const checkIdentity = useCheckIdentity();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    projectId: '',
    firstName: '',
    lastNamePaternal: '',
    lastNameMaternal: '',
    phone: '',
    email: '',
    documentType: '' as '' | DocumentType,
    documentNumber: '',
    district: '',
    /** Medio elegido del catálogo, `OTHER_MEDIUM` para texto libre o vacío. */
    medium: '',
    mediumOther: '',
    entryChannel: '',
    advisorId: isManager ? '' : (user?.id ?? ''),
    note: '',
  });
  // Consentimiento al registrar: tres estados explícitos. Por defecto «no se preguntó» (no se manda nada): el registro a
  // mano no implica consentimiento ni levanta un «no contactar» previo; el opt-out exige motivo y bloquea WhatsApp y bot.
  const [consentMode, setConsentMode] = useState<'UNKNOWN' | 'GRANTED' | 'OPT_OUT'>('UNKNOWN');
  const [optOutReason, setOptOutReason] = useState('');
  // Campos personalizados y etiquetas de la empresa elegida (se limpian al cambiar de empresa).
  const customFields = catalogs?.customFields ?? [];
  const tagOptions = catalogs?.tags ?? [];
  const [customValues, setCustomValues] = useState<Record<string, string>>({});
  const [customErrors, setCustomErrors] = useState<Record<string, string>>({});
  const [tagIds, setTagIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    setCustomValues({});
    setCustomErrors({});
    setTagIds(new Set());
  }, [effectiveCompany]);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const visibleProjects = (projects ?? []).filter((p) => p.companyId === effectiveCompany);

  // Medio: selector del catálogo agrupado por categoría + «Otro…» con texto libre y aviso de la categoría.
  const media = useMemo(() => catalogs?.media ?? [], [catalogs]);
  const mediumGroups = useMemo(() => {
    const cats = catalogs?.mediumCategories ?? [];
    const groups = cats
      .map((c) => ({ ...c, media: media.filter((m) => m.categoryCode === c.code) }))
      .filter((g) => g.media.length > 0);
    const known = new Set(cats.map((c) => c.code));
    const loose = media.filter((m) => !known.has(m.categoryCode));
    if (loose.length)
      groups.push({ code: '', name: 'Otros medios', channel: '', isDigital: false, media: loose });
    return groups;
  }, [catalogs, media]);
  const mediumValue =
    form.medium === OTHER_MEDIUM ? form.mediumOther.trim() || null : form.medium || null;
  const otherMediumHint = useMemo(() => {
    if (form.medium !== OTHER_MEDIUM) return null;
    const text = form.mediumOther.trim();
    if (!text) return null;
    const canonical = resolveMediumName(text, media);
    const code = resolveMediumCategory(text, media, 'OTROS');
    const cat = catalogs?.mediumCategories.find((c) => c.code === code);
    if (canonical)
      return {
        tone: 'ok' as const,
        text: `Es un medio del catálogo: se guardará como «${canonical}».`,
      };
    if (code && code !== 'OTROS')
      return {
        tone: 'ok' as const,
        text: `Se guardará en la categoría ${cat?.name ?? code}.`,
      };
    return {
      tone: 'warn' as const,
      text: 'Se guardará en la categoría OTROS (no cuenta como lead digital ni se agrupa con ningún medio del catálogo).',
    };
  }, [form.medium, form.mediumOther, media, catalogs]);

  // Asesor: con regla por turnos activa, el valor por defecto es «según el turno» y aparte «dejar en la bolsa».
  const { data: nextAdvisor } = useNextAdvisor(
    isManager ? effectiveCompany || undefined : undefined,
    form.projectId || undefined,
  );
  const byTurn = !!nextAdvisor?.active;

  // Aviso de persona repetida (G1): comprobación exacta en la API con los datos completos, con retardo al escribir.
  const probe = identityProbe(form);
  const probeKey = probe
    ? `${effectiveCompany}|${form.projectId}|${probe.documentNumber ?? ''}|${probe.phone ?? ''}|${probe.email ?? ''}`
    : '';
  const [identity, setIdentity] = useState<{ key: string; result: CheckIdentityResponse } | null>(
    null,
  );
  const checkRef = useRef(checkIdentity.mutateAsync);
  checkRef.current = checkIdentity.mutateAsync;
  const runCheck = useCallback(
    async (key: string): Promise<CheckIdentityResponse | null> => {
      if (!key) {
        setIdentity(null);
        return null;
      }
      const [company, projectId, documentNumber, phone, email] = key.split('|');
      if (!company || !projectId) return null;
      try {
        const result = await checkRef.current({
          companyId: company,
          projectId,
          documentNumber: documentNumber || null,
          documentType: form.documentType || null,
          phone: phone || null,
          email: email || null,
        });
        setIdentity({ key, result });
        return result;
      } catch {
        return null;
      }
    },
    [form.documentType],
  );
  useEffect(() => {
    const t = setTimeout(() => void runCheck(probeKey), 400);
    return () => clearTimeout(t);
  }, [probeKey, runCheck]);
  const current = identity && identity.key === probeKey ? identity.result : null;
  const duplicates: CheckIdentityMatch[] = current?.matches ?? [];
  // 7.27: con una persona repetida hay que confirmar a propósito; la API puede además bloquearla (ajuste de la empresa).
  const [acknowledged, setAcknowledged] = useState(false);
  const [blocked, setBlocked] = useState<{
    existingLeadId: string;
    projectName: string;
    advisorName: string | null;
  } | null>(null);
  useEffect(() => {
    setAcknowledged(false);
    setBlocked(null);
  }, [probeKey]);

  /** Lleva la vista al primer campo con error (o al bloque general) y lo enfoca. */
  function focusFirstError(errs: Record<string, string>) {
    const path = Object.keys(errs).find((k) => k !== 'form' && FIELD_IDS[k]);
    const el = path
      ? document.getElementById(FIELD_IDS[path]!)
      : document.getElementById('form-error');
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    if (el instanceof HTMLElement && path) el.focus({ preventScroll: true });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    // «Dejar en la bolsa» solo cuando se eligió a propósito: con el valor por defecto manda el turno del proyecto
    // (si no hay regla, la API lo deja en la bolsa igual), aunque «siguiente» aún esté cargando o haya fallado.
    const keepInPool = isManager && form.advisorId === POOL;
    const advisorId =
      form.advisorId && form.advisorId !== POOL
        ? form.advisorId
        : isManager
          ? null
          : (user?.id ?? null);
    const payload: CreateLeadPayload = {
      companyId: effectiveCompany,
      projectId: form.projectId,
      contact: {
        firstName: form.firstName,
        lastNamePaternal: form.lastNamePaternal || null,
        lastNameMaternal: form.lastNameMaternal || null,
        phone: form.phone || null,
        email: form.email || null,
        documentType: form.documentType || null,
        documentNumber: form.documentNumber || null,
        district: form.district || null,
      },
      medium: mediumValue,
      entryChannel: form.entryChannel || null,
      utmSource: null,
      utmMedium: null,
      utmCampaign: null,
      utmTerm: null,
      utmContent: null,
      advisorId,
      keepInPool: keepInPool || undefined,
      note: form.note || null,
      reuseActiveLead: true,
      consent:
        consentMode === 'GRANTED'
          ? { granted: true, source: 'MANUAL', reason: null }
          : consentMode === 'OPT_OUT'
            ? { granted: false, source: 'MANUAL', reason: optOutReason || null }
            : undefined,
      // Un campo vacío no se manda: en un recontacto nunca borra lo que el lead ya tenía.
      custom: customFields.length
        ? customPayload(customFields, customValues, { omitEmpty: true })
        : undefined,
      tags: tagIds.size ? [...tagIds] : undefined,
      acknowledgeDuplicate: acknowledged || undefined,
    };
    const cfErrors = customFormErrors(customFields, customValues);
    setCustomErrors(cfErrors);
    if (Object.keys(cfErrors).length) {
      const errs = { form: 'Completa los datos adicionales obligatorios' };
      setErrors(errs);
      document.getElementById('new-lead-extras')?.scrollIntoView({ block: 'center' });
      return;
    }
    const parsed = createLeadSchema.safeParse(payload);
    if (!parsed.success) {
      // Cada error va bajo su campo; los que no tienen campo visible se listan en el bloque general.
      const errs: Record<string, string> = {};
      const orphan: string[] = [];
      for (const issue of parsed.error.issues) {
        const path = issue.path.join('.');
        errs[path] = issue.message;
        if (!FIELD_IDS[path]) orphan.push(`${path}: ${issue.message}`);
      }
      errs.form = orphan.length
        ? `Revisa estos datos: ${orphan.join(' · ')}`
        : 'Revisa los campos marcados en rojo.';
      setErrors(errs);
      focusFirstError(errs);
      return;
    }
    // Antes de guardar se espera la comprobación de identidad con los valores actuales (no la del último tecleo).
    const latest = current ?? (await runCheck(probeKey));
    if ((latest?.matches.length ?? 0) > 0 && !acknowledged) {
      const errs = {
        form: 'Esta persona ya existe: abre el lead existente o marca «Registrar de todos modos».',
      };
      setErrors(errs);
      document.getElementById('duplicate-hint')?.scrollIntoView({ block: 'center' });
      return;
    }
    setErrors({});
    setBlocked(null);
    try {
      const result = await mutation.mutateAsync(parsed.data);
      // La empresa del lead recién creado pasa a ser la activa: el listado y el kanban lo muestran de inmediato.
      setActive(effectiveCompany);
      if (result.outOfScope) {
        // Lead ajeno: no se tocó, se avisó a su asesor y no hay ficha que abrir para quien registró.
        toast.push({
          tone: 'info',
          title: 'La persona ya tiene asesor',
          description: result.message ?? undefined,
        });
        navigate(`/pipeline?vista=tabla&companyId=${effectiveCompany}`);
        return;
      }
      const extra = result.contactNotes.length ? ` ${result.contactNotes.join('. ')}.` : '';
      if (result.reusedExistingLead)
        toast.success(
          'Recontacto registrado',
          `La persona ya tenía un lead activo en ${result.projectName}${result.advisorName ? ` con ${result.advisorName}` : ''}; se anotó el recontacto${result.advisorName && result.advisorId !== user?.id ? ' y se le avisó' : ''}.${extra}`,
        );
      else if (result.isRecontact)
        toast.success(
          'Lead creado como recontacto',
          `La persona ya existía en la empresa; hereda el medio original. ${assignmentText(result)}${extra}`,
        );
      else toast.success('Lead creado', `${assignmentText(result)}${extra}`);
      navigate(`/leads/${result.leadId}`);
    } catch (err) {
      const details =
        err instanceof ApiRequestError
          ? (
              err.body as {
                details?: {
                  fields?: Record<string, string>;
                  code?: string;
                  existingLeadId?: string | null;
                  projectName?: string | null;
                  advisorName?: string | null;
                  contactId?: string;
                  name?: string;
                  matchedOn?: CheckIdentityMatch['matchedOn'];
                  matchedValue?: string;
                  restricted?: boolean;
                };
              }
            ).details
          : undefined;
      if (details?.fields) setCustomErrors(details.fields);
      if (details?.code === 'DUPLICATE_BLOCKED' && details.existingLeadId)
        setBlocked({
          existingLeadId: details.existingLeadId,
          projectName: details.projectName ?? '',
          advisorName: details.advisorName ?? null,
        });
      if (details?.code === 'DUPLICATE_CONFIRM') {
        // La API encontró a la persona (p. ej. por un dato incompleto que la web no consulta, como un fijo de 8
        // dígitos o un documento de 6): se muestra lo que devolvió y se pide confirmar. Se siembra siempre con el
        // 409, aunque haya otro dato completo: la comprobación con ese dato puede no traer esta coincidencia y
        // sin ella no aparecería la casilla «Registrar de todos modos».
        setAcknowledged(false);
        const seeded: CheckIdentityResponse = {
          matches: [
            {
              contactId: details.contactId ?? '',
              name: details.name ?? '',
              phone: null,
              email: null,
              documentNumber: null,
              matchedOn: details.matchedOn ?? 'TELEFONO',
              matchedValue: details.matchedValue ?? '',
              doNotContact: false,
              restricted: details.restricted ?? false,
              leads: details.existingLeadId
                ? [
                    {
                      id: details.existingLeadId,
                      projectId: '',
                      projectName: details.projectName ?? '',
                      status: null,
                      pipelineStage: null,
                      pipelineStageLabel: null,
                      advisorId: null,
                      advisorName: details.advisorName ?? null,
                      visible: !details.restricted,
                      wouldReuse: false,
                      capturedAt: null,
                    },
                  ]
                : [],
            },
          ],
          wouldReuseLeadId: null,
          outOfScope: false,
        };
        setIdentity({ key: probeKey, result: seeded });
        if (probeKey)
          void runCheck(probeKey).then((r) => {
            if (!r || r.matches.length === 0) setIdentity({ key: probeKey, result: seeded });
          });
      }
      const errs = {
        form: err instanceof ApiRequestError ? err.message : 'No se pudo crear el lead',
      };
      setErrors(errs);
      focusFirstError(errs);
    }
  }

  const err = (k: string) =>
    errors[k] ? <p className="text-destructive text-xs">{errors[k]}</p> : null;
  const invalid = (k: string) => (errors[k] ? 'border-destructive' : undefined);

  return (
    <form onSubmit={submit} className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Nuevo lead</h1>
        <p className="text-muted-foreground text-sm">
          Si la persona ya existe (por documento, celular o correo) se registra como recontacto,
          nunca como duplicado.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Proyecto y origen</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          {companies.length > 1 && (
            <div className="space-y-1">
              <Label htmlFor="company">Empresa</Label>
              <Select
                id="company"
                value={effectiveCompany}
                onChange={(e) => {
                  const next = e.target.value;
                  setCompanyId(next);
                  // Proyecto, asesor, medio y canal son de la empresa anterior: se reinician (si no, la API
                  // rechaza «El asesor no pertenece a la empresa» sin que se vea ningún asesor elegido).
                  const managesNext = !!user?.memberships.some(
                    (m) => m.companyId === next && MANAGER_ROLES.includes(m.role),
                  );
                  setForm((f) => ({
                    ...f,
                    projectId: '',
                    advisorId: managesNext ? '' : (user?.id ?? ''),
                    medium: '',
                    mediumOther: '',
                    entryChannel: '',
                  }));
                }}
              >
                {companies.map((c) => (
                  <option key={c.companyId} value={c.companyId}>
                    {c.companyName}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="project">Proyecto</Label>
            <Select
              id="project"
              value={form.projectId}
              onChange={(e) => set('projectId', e.target.value)}
              required
              className={invalid('projectId')}
            >
              <option value="">Selecciona…</option>
              {visibleProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
            {err('projectId')}
          </div>
          <div className="space-y-1">
            <Label htmlFor="medium">Medio de captación</Label>
            <Select
              id="medium"
              value={form.medium}
              onChange={(e) => set('medium', e.target.value)}
              data-testid="medium-select"
            >
              <option value="">Selecciona…</option>
              {mediumGroups.map((g) => (
                <optgroup key={g.code || 'otros'} label={g.name}>
                  {g.media.map((m) => (
                    <option key={m.name} value={m.name}>
                      {m.name}
                    </option>
                  ))}
                </optgroup>
              ))}
              <option value={OTHER_MEDIUM}>Otro…</option>
            </Select>
            {form.medium === OTHER_MEDIUM && (
              <div className="space-y-1">
                <Input
                  id="medium-other"
                  aria-label="Otro medio"
                  value={form.mediumOther}
                  onChange={(e) => set('mediumOther', e.target.value)}
                  placeholder="Escribe el medio…"
                  maxLength={TEXT_MAX}
                  autoFocus
                  className={invalid('medium')}
                  data-testid="medium-other"
                />
                {otherMediumHint && (
                  <p
                    className={`text-xs ${otherMediumHint.tone === 'warn' ? 'text-warning' : 'text-muted-foreground'}`}
                    data-testid="medium-hint"
                  >
                    {otherMediumHint.text}
                  </p>
                )}
                {isAdmin && (
                  <p className="text-muted-foreground text-xs">
                    ¿Es un medio que se repite?{' '}
                    <Link
                      to={`/admin?tab=catalogos&companyId=${effectiveCompany}#medios`}
                      className="text-primary underline"
                    >
                      Agrégalo al catálogo
                    </Link>{' '}
                    para que cuente en su categoría.
                  </p>
                )}
              </div>
            )}
            {err('medium')}
          </div>
          <div className="space-y-1">
            <Label htmlFor="channel">Canal de entrada</Label>
            <Select
              id="channel"
              value={form.entryChannel}
              onChange={(e) => set('entryChannel', e.target.value)}
            >
              <option value="">Selecciona…</option>
              {catalogs?.entryChannels.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </Select>
          </div>
          {isManager && (
            <div className="space-y-1">
              <Label htmlFor="advisor">Asesor</Label>
              <Select
                id="advisor"
                value={form.advisorId}
                onChange={(e) => set('advisorId', e.target.value)}
              >
                {byTurn ? (
                  <>
                    <option value="">
                      Según el turno del proyecto
                      {nextAdvisor?.advisorName ? ` · siguiente: ${nextAdvisor.advisorName}` : ''}
                    </option>
                    <option value={POOL}>Dejar en la bolsa (sin asesor)</option>
                  </>
                ) : (
                  <option value="">Sin asignar (bolsa)</option>
                )}
                {advisors
                  ?.filter((a) => a.kind === 'HUMAN')
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.fullName}
                    </option>
                  ))}
              </Select>
              {byTurn && !form.advisorId && (
                <p className="text-muted-foreground text-xs" data-testid="advisor-turn-hint">
                  El proyecto reparte por turnos: el lead irá al siguiente asesor
                  {nextAdvisor?.advisorName ? ` (${nextAdvisor.advisorName})` : ''}. Elige «Dejar en
                  la bolsa» si no quieres asignarlo todavía.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Persona</CardTitle>
          <CardDescription>Al menos celular, correo o documento.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="firstName">Nombres</Label>
            <Input
              id="firstName"
              value={form.firstName}
              onChange={(e) => set('firstName', e.target.value)}
              required
              autoFocus
              maxLength={TEXT_MAX}
              className={invalid('contact.firstName')}
            />
            {err('contact.firstName')}
          </div>
          <div className="space-y-1">
            <Label htmlFor="lastNamePaternal">Apellido paterno</Label>
            <Input
              id="lastNamePaternal"
              value={form.lastNamePaternal}
              onChange={(e) => set('lastNamePaternal', e.target.value)}
              maxLength={TEXT_MAX}
              className={invalid('contact.lastNamePaternal')}
            />
            {err('contact.lastNamePaternal')}
          </div>
          <div className="space-y-1">
            <Label htmlFor="lastNameMaternal">Apellido materno</Label>
            <Input
              id="lastNameMaternal"
              value={form.lastNameMaternal}
              onChange={(e) => set('lastNameMaternal', e.target.value)}
              maxLength={TEXT_MAX}
              className={invalid('contact.lastNameMaternal')}
            />
            {err('contact.lastNameMaternal')}
          </div>
          <div className="space-y-1">
            <Label htmlFor="phone">Celular</Label>
            <Input
              id="phone"
              inputMode="tel"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              placeholder="987 654 321"
              maxLength={30}
              className={invalid('contact.phone')}
            />
            {err('contact.phone')}
          </div>
          <div className="space-y-1">
            <Label htmlFor="email">Correo</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              maxLength={120}
              className={invalid('contact.email')}
            />
            {err('contact.email')}
          </div>
          <div className="space-y-1">
            <Label htmlFor="district">Distrito</Label>
            <Input
              id="district"
              value={form.district}
              onChange={(e) => set('district', e.target.value)}
              maxLength={TEXT_MAX}
              className={invalid('contact.district')}
            />
            {err('contact.district')}
          </div>
          <div className="space-y-1">
            <Label htmlFor="docType">Tipo de documento</Label>
            <Select
              id="docType"
              value={form.documentType}
              onChange={(e) => set('documentType', e.target.value)}
            >
              <option value="">—</option>
              {Object.values(DocumentType).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="docNumber">Número de documento</Label>
            <Input
              id="docNumber"
              value={form.documentNumber}
              onChange={(e) => set('documentNumber', e.target.value)}
              maxLength={20}
              className={invalid('contact.documentNumber')}
            />
            {err('contact.documentNumber')}
          </div>
          {checkIdentity.isPending && duplicates.length === 0 && (
            <p className="text-muted-foreground text-xs sm:col-span-3" aria-live="polite">
              Comprobando si la persona ya existe…
            </p>
          )}
          {duplicates.length > 0 && (
            <div
              id="duplicate-hint"
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm sm:col-span-3 dark:bg-amber-950/30"
              data-testid="duplicate-hint"
            >
              <p className="flex items-center gap-1 font-medium">
                <AlertTriangle className="text-warning h-4 w-4" /> Esta persona ya existe en la
                empresa
              </p>
              <ul className="mt-1 space-y-1.5">
                {duplicates.slice(0, 3).map((m) => (
                  <li key={m.contactId}>
                    <span className="font-medium">{m.name}</span>{' '}
                    <span className="text-muted-foreground">
                      · coincide por {IDENTITY_MATCH_LABELS[m.matchedOn]} {m.matchedValue}
                      {m.matchedOn !== 'TELEFONO' && m.phone ? ` · cel. ${m.phone}` : ''}
                      {m.matchedOn !== 'CORREO' && m.email ? ` · ${m.email}` : ''}
                      {m.matchedOn !== 'DOCUMENTO' && m.documentNumber
                        ? ` · doc. ${m.documentNumber}`
                        : ''}
                      {m.doNotContact ? ' · pidió no ser contactada' : ''}
                      {m.restricted
                        ? ' · sus datos de contacto no se muestran (no ves sus leads)'
                        : ''}
                    </span>
                    {m.leads.length > 0 ? (
                      <ul className="mt-0.5 space-y-0.5 pl-4">
                        {m.leads.slice(0, 4).map((l) => (
                          <li key={l.id} className="text-xs">
                            {l.visible ? (
                              <Link to={`/leads/${l.id}`} className="text-primary underline">
                                {l.projectName}
                              </Link>
                            ) : (
                              <span>{l.projectName}</span>
                            )}
                            <span className="text-muted-foreground">
                              {l.pipelineStageLabel ? ` · ${l.pipelineStageLabel}` : ''}
                              {l.status ? ` · ${l.status === 'ACTIVO' ? 'activo' : 'cerrado'}` : ''}
                              {' · '}
                              {l.advisorName ? `con ${l.advisorName}` : 'sin asesor'}
                              {l.visible ? '' : ' · no lo puedes ver'}
                            </span>
                            {l.wouldReuse && (
                              <span className="ml-1 font-medium">
                                · se anotará el recontacto en este lead
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-muted-foreground text-xs"> · sin leads</span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground mt-1 text-xs">
                {current?.outOfScope
                  ? 'Esa persona ya la atiende otro asesor en este proyecto y no puedes ver ese lead: si continúas, se le avisará a su asesor y no se creará otro lead.'
                  : current?.wouldReuseLeadId
                    ? 'Si continúas, se anotará el recontacto en el lead activo de este proyecto (conserva a su asesor; si está en la bolsa, lo toma el asesor indicado), nunca como duplicado.'
                    : 'Si continúas, se creará un lead nuevo como recontacto (hereda el medio original), nunca como duplicado.'}
              </p>
              <label className="mt-2 flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  data-testid="duplicate-ack"
                />
                Registrar de todos modos
              </label>
            </div>
          )}
          {blocked && (
            <div
              className="border-destructive/50 bg-destructive/10 rounded-md border p-3 text-sm sm:col-span-3"
              role="alert"
              data-testid="duplicate-blocked"
            >
              <p className="font-medium">
                La empresa no permite registrar de nuevo a esta persona.
              </p>
              <p>
                Ya tiene un lead activo en {blocked.projectName}
                {blocked.advisorName ? ` con ${blocked.advisorName}` : ''}.{' '}
                <Link to={`/leads/${blocked.existingLeadId}`} className="text-primary underline">
                  Abrir el lead existente
                </Link>
              </p>
            </div>
          )}
          <fieldset className="space-y-1 text-sm sm:col-span-3" data-testid="consent-mode">
            <legend className="text-sm font-medium">Consentimiento de contacto</legend>
            {(
              [
                [
                  'UNKNOWN',
                  'No se le preguntó',
                  'No se registra nada; puedes anotarlo después en la ficha.',
                ],
                [
                  'GRANTED',
                  'Autorizó que le contactemos',
                  'Queda registrado el consentimiento (fuente: registro manual).',
                ],
                [
                  'OPT_OUT',
                  'Pidió que no le escribamos',
                  'WhatsApp y el bot quedan bloqueados hasta un nuevo consentimiento explícito.',
                ],
              ] as const
            ).map(([value, label, hint]) => (
              <label key={value} className="flex items-start gap-2">
                <input
                  type="radio"
                  name="consent-mode"
                  value={value}
                  checked={consentMode === value}
                  onChange={() => setConsentMode(value)}
                  className="mt-0.5 h-4 w-4"
                  data-testid={`consent-${value.toLowerCase()}`}
                />
                <span>
                  {label} <span className="text-muted-foreground text-xs">· {hint}</span>
                </span>
              </label>
            ))}
            {consentMode === 'OPT_OUT' && (
              <div className="space-y-1 pt-1">
                <Label htmlFor="optout-reason">Motivo del «no contactar»</Label>
                <Input
                  id="optout-reason"
                  value={optOutReason}
                  onChange={(e) => setOptOutReason(e.target.value)}
                  placeholder="Lo pidió por teléfono el 5/10; no quiere publicidad…"
                  required
                  maxLength={300}
                  data-testid="optout-reason"
                />
                {err('consent.reason')}
              </div>
            )}
          </fieldset>
          <div className="space-y-1 sm:col-span-3">
            <div className="flex items-baseline justify-between">
              <Label htmlFor="note">Nota inicial</Label>
              <span
                className={`text-xs ${form.note.length >= NOTE_MAX ? 'text-warning' : 'text-muted-foreground'}`}
                data-testid="note-counter"
              >
                {form.note.length} / {NOTE_MAX}
              </span>
            </div>
            <Textarea
              id="note"
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
              rows={2}
              maxLength={NOTE_MAX}
              placeholder="¿Qué busca? ¿Cómo llegó?"
              className={invalid('note')}
            />
            {err('note')}
          </div>
        </CardContent>
      </Card>
      {(customFields.length > 0 || tagOptions.length > 0) && (
        <Card data-testid="new-lead-extras" id="new-lead-extras">
          <CardHeader>
            <CardTitle>Datos adicionales</CardTitle>
            <CardDescription>
              Campos y etiquetas propios de la empresa. Lo que dejes vacío no borra nada si la
              persona ya tenía un lead.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-3">
            <CustomFieldInputs
              fields={customFields}
              values={customValues}
              errors={customErrors}
              onChange={(k, v) => setCustomValues((c) => ({ ...c, [k]: v }))}
              idPrefix="new-cf"
            />
            {tagOptions.length > 0 && (
              <div className="space-y-1 sm:col-span-3">
                <Label>Etiquetas</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="new-lead-tags">
                  {tagOptions.map((t) => {
                    const on = tagIds.has(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        aria-pressed={on}
                        onClick={() =>
                          setTagIds((s) => {
                            const n = new Set(s);
                            if (n.has(t.id)) n.delete(t.id);
                            else n.add(t.id);
                            return n;
                          })
                        }
                        className={`rounded-full ${on ? 'ring-primary ring-2 ring-offset-1' : 'opacity-70 hover:opacity-100'}`}
                      >
                        <TagChip tag={t} />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {errors.form && (
        <p
          id="form-error"
          role="alert"
          className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
        >
          {errors.form}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => navigate(-1)}>
          Cancelar
        </Button>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Crear lead
        </Button>
      </div>
    </form>
  );
}

/** Texto del aviso al guardar: a quién se asignó y por qué, o que quedó en la bolsa. */
function assignmentText(r: CreateLeadResult): string {
  switch (r.assignment) {
    case 'TURNO':
      return `Asignado a ${r.advisorName ?? 'un asesor'} por turno del proyecto.`;
    case 'INDICADO':
      return r.advisorName ? `Asignado a ${r.advisorName}.` : '';
    case 'BOLSA':
      return 'Quedó en la bolsa, sin asesor.';
    default:
      return r.advisorName ? `Sigue con ${r.advisorName}.` : '';
  }
}
